import { useEffect, useState } from "react";
import { api, formatCurrency, formatDate } from "../lib/api";
import { PageHeader, StatusBadge } from "../components/ui-kit";
import { useAuth } from "../lib/auth";
import { AlertTriangle, ClipboardList, CheckCircle2, TrendingUp, TrendingDown, Award, Building2, Wrench, Truck, Router as RouterIcon, ArrowRightLeft } from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [prod, setProd] = useState(null);
  const [techMonthly, setTechMonthly] = useState(null);
  const [byOperator, setByOperator] = useState(null);
  const [maintenance, setMaintenance] = useState(null);

  const isTech = user?.role === "tecnico";
  const isAlmacenero = user?.role === "almacenero";
  const isManager = user?.role === "admin" || user?.role === "gestor";

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    if (!isAlmacenero) {
      api.get("/reports/production").then((r) => setProd(r.data)).catch(() => {});
    }
    if (isTech) {
      api.get("/dashboard/tech-monthly").then((r) => setTechMonthly(r.data)).catch(() => {});
    }
    if (isManager) {
      api.get("/dashboard/by-operator").then((r) => setByOperator(r.data)).catch(() => {});
      api.get("/maintenance/alerts").then((r) => setMaintenance(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Almacenero: warehouse-focused dashboard (no financial data / no OTs)
  if (isAlmacenero) {
    return (
      <div data-testid="dashboard-page">
        <PageHeader eyebrow={`Bem-vindo, ${user?.name}`} title="Dashboard do Armazém">
          Visão geral do armazém e dos materiais
        </PageHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard icon={<RouterIcon size={18} />} label="Equipamentos em Estoque" value={data?.equipment_in_stock ?? "-"} testid="stat-eq-stock" />
          <StatCard icon={<ArrowRightLeft size={18} />} label="Traspassos Pendentes" value={data?.pending_transfers ?? "-"} testid="stat-pending-transfers" />
          <StatCard icon={<AlertTriangle size={18} />} label="Materiais Críticos" value={data?.critical_stock?.length ?? 0} testid="stat-critical-count" />
        </div>

        <div className="card" data-testid="critical-stock">
          <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
            <AlertTriangle size={15} color="#FACC15" />
            <h3 className="text-sm label-caps">Estoque Crítico (&lt; 10)</h3>
          </div>
          {data?.critical_stock?.length ? (
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {data.critical_stock.map((m) => (
                <div key={m.id} className="px-5 py-3 flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
                  <div>
                    <div className="text-sm">{m.name}</div>
                    <div className="text-xs mono" style={{ color: "var(--text-mute)" }}>{m.code}</div>
                  </div>
                  <div className="mono font-semibold" style={{ color: "#FACC15" }}>{m.stock}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-10 text-center text-sm" style={{ color: "var(--text-mute)" }}>Nenhum item crítico.</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={`Bem-vindo, ${user?.name}`} title="Dashboard">
        Visão geral das operações em campo
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={<ClipboardList size={18} />} label="OTs Ativas" value={data?.open_count ?? "-"} testid="stat-open" />
        <StatCard icon={<CheckCircle2 size={18} />} label="OTs Concluídas" value={data?.done_count ?? "-"} testid="stat-done" />
        <StatCard icon={<TrendingUp size={18} />} label={isTech ? "Minha Produção (7d)" : "Faturamento Semana"} value={prod ? formatCurrency(prod.weekly.value) : "-"} testid="stat-week" />
        <StatCard icon={<TrendingUp size={18} />} label={isTech ? "Minha Produção (Mês)" : "Faturamento Mês"} value={prod ? formatCurrency(prod.monthly.value) : "-"} testid="stat-month" />
      </div>

      {isTech && techMonthly && <TechMonthlyCard data={techMonthly} />}
      {isManager && byOperator && byOperator.length > 0 && <OperatorPanel data={byOperator} />}
      {isManager && maintenance && <MaintenanceAlerts data={maintenance} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card lg:col-span-2" data-testid="recent-ots">
          <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <h3 className="text-sm label-caps">Últimas OTs</h3>
          </div>
          {data?.recent_ots?.length ? (
            <table className="data">
              <thead>
                <tr><th>OT</th><th>Data</th><th>Cliente</th><th>Serviço</th><th>Valor</th><th>Status</th></tr>
              </thead>
              <tbody>
                {data.recent_ots.map((o) => (
                  <tr key={o.id}>
                    <td className="mono">{o.number}</td>
                    <td>{formatDate(o.date)}</td>
                    <td>{o.client_name}</td>
                    <td>{o.service_type_name}</td>
                    <td className="mono">{formatCurrency(o.total_value)}</td>
                    <td><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-10 text-center text-sm" style={{ color: "var(--text-mute)" }}>Nenhuma OT registrada ainda.</div>
          )}
        </div>

        {isManager && (
          <div className="card" data-testid="critical-stock">
            <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
              <AlertTriangle size={15} color="#FACC15" />
              <h3 className="text-sm label-caps">Estoque Crítico (&lt; 10)</h3>
            </div>
            {data?.critical_stock?.length ? (
              <div className="divide-y" style={{ borderColor: "var(--border)" }}>
                {data.critical_stock.map((m) => (
                  <div key={m.id} className="px-5 py-3 flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
                    <div>
                      <div className="text-sm">{m.name}</div>
                      <div className="text-xs mono" style={{ color: "var(--text-mute)" }}>{m.code}</div>
                    </div>
                    <div className="mono font-semibold" style={{ color: "#FACC15" }}>{m.stock}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-10 text-center text-sm" style={{ color: "var(--text-mute)" }}>Nenhum item crítico.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3" style={{ color: "var(--text-mute)" }}>
        {icon}<span className="stat-label">{label}</span>
      </div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function TechMonthlyCard({ data }) {
  const { current, previous, delta_percent } = data;
  const positive = delta_percent === null || delta_percent >= 0;
  const arrowIcon = positive ? <TrendingUp size={18} /> : <TrendingDown size={18} />;
  const color = delta_percent === null ? "var(--text-mute)" : positive ? "#4ADE80" : "#F87171";
  return (
    <div className="card mb-8 p-6" data-testid="tech-monthly-card" style={{ background: "linear-gradient(135deg, rgba(56,189,248,0.06), rgba(56,189,248,0.02))" }}>
      <div className="flex items-center gap-2 mb-4">
        <Award size={18} style={{ color: "var(--primary)" }} />
        <span className="label-caps">Ranking Mensal</span>
        <span className="text-xs mono ml-auto" style={{ color: "var(--text-mute)" }}>{current.month}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
        <div>
          <div className="stat-label mb-1" style={{ color: "var(--text-dim)" }}>Este mês</div>
          <div className="text-4xl font-bold mono" data-testid="tech-current-total">{formatCurrency(current.total)}</div>
          <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>{current.count} OT(s) · {previous.count > 0 ? `${previous.count} no mês passado` : "-"}</div>
        </div>
        <div>
          <div className="stat-label mb-1" style={{ color: "var(--text-dim)" }}>Mês anterior · {previous.month}</div>
          <div className="text-2xl font-semibold mono" style={{ color: "var(--text-dim)" }} data-testid="tech-previous-total">{formatCurrency(previous.total)}</div>
        </div>
        <div className="flex items-center gap-2 justify-end" style={{ color }} data-testid="tech-delta">
          {arrowIcon}
          <span className="text-2xl font-bold mono">
            {delta_percent === null ? "novo" : (positive ? "+" : "") + delta_percent + "%"}
          </span>
        </div>
      </div>
    </div>
  );
}

function OperatorPanel({ data }) {
  const grand = data.reduce((acc, o) => ({
    revenue: acc.revenue + (o.company_revenue || 0),
    cost: acc.cost + (o.technician_cost || 0),
    ots: acc.ots + (o.ots_count || 0),
  }), { revenue: 0, cost: 0, ots: 0 });
  return (
    <div className="card mb-8" data-testid="operator-panel">
      <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
        <Building2 size={15} />
        <h3 className="text-sm label-caps">Comparativo por Operadora — Mês Atual</h3>
        <span className="ml-auto text-xs mono" style={{ color: "var(--text-mute)" }}>
          {grand.ots} OTs · {formatCurrency(grand.revenue)} bruto · {formatCurrency(grand.revenue - grand.cost)} líquido
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
        {data.map((op) => {
          const share = grand.revenue > 0 ? Math.round((op.company_revenue / grand.revenue) * 100) : 0;
          return (
            <div key={op.id} className="border p-4" style={{ borderColor: "var(--border-strong)", background: "var(--surface-2)" }} data-testid={`operator-card-${op.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <span style={{ width: 10, height: 10, borderRadius: 999, background: op.color || "var(--primary)" }} />
                <b className="text-sm">{op.name}</b>
                {op.code && <span className="mono text-[10px]" style={{ color: "var(--text-mute)" }}>· {op.code}</span>}
                {!op.active && <span className="badge badge-cancel ml-auto">Inativa</span>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="label-caps mb-0.5">OTs Mês</div>
                  <div className="text-xl font-bold mono">{op.ots_count}</div>
                  <div className="text-[10px]" style={{ color: "var(--text-mute)" }}>{op.ots_completed} concluída(s)</div>
                </div>
                <div>
                  <div className="label-caps mb-0.5">Técnicos</div>
                  <div className="text-xl font-bold mono">{op.active_technicians}</div>
                </div>
                <div className="col-span-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                  <div className="flex justify-between text-[11px]"><span style={{ color: "var(--text-mute)" }}>Faturamento empresa</span><span className="mono font-semibold">{formatCurrency(op.company_revenue)}</span></div>
                  <div className="flex justify-between text-[11px]"><span style={{ color: "var(--text-mute)" }}>Custo técnicos</span><span className="mono">-{formatCurrency(op.technician_cost)}</span></div>
                  <div className="flex justify-between text-sm mt-1 pt-1 border-t" style={{ borderColor: "var(--border)" }}><span className="label-caps">Líquido</span><span className="mono font-bold" style={{ color: "#4ADE80" }}>{formatCurrency(op.net)}</span></div>
                </div>
                {grand.revenue > 0 && (
                  <div className="col-span-2">
                    <div className="text-[10px]" style={{ color: "var(--text-mute)" }}>{share}% do faturamento total</div>
                    <div style={{ height: 4, background: "var(--border)", marginTop: 3 }}>
                      <div style={{ width: `${share}%`, height: "100%", background: op.color || "var(--primary)" }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MaintenanceAlerts({ data }) {
  const { alerts, settings } = data;
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="card mb-8" data-testid="maintenance-alerts">
      <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
        <AlertTriangle size={15} color="#F97316" />
        <h3 className="text-sm label-caps">Alertas de Manutenção</h3>
        <span className="ml-auto text-[10px]" style={{ color: "var(--text-mute)" }}>
          limite: {settings.vehicle_km_threshold} km · {settings.tool_out_months_threshold} meses
        </span>
      </div>
      <div className="divide-y" style={{ borderColor: "var(--border)" }}>
        {alerts.map((a) => (
          <div key={`${a.kind}-${a.id}`} className="px-5 py-3 flex items-center gap-3" data-testid={`maint-alert-${a.id}`}>
            <div style={{ color: a.severity === "high" ? "#F87171" : "#F97316" }}>
              {a.kind === "vehicle_km" ? <Truck size={16} /> : <Wrench size={16} />}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">{a.title}</div>
              <div className="text-xs" style={{ color: "var(--text-mute)" }}>{a.detail}</div>
            </div>
            <span className="badge" style={{
              background: a.severity === "high" ? "rgba(248,113,113,0.12)" : "rgba(249,115,22,0.12)",
              color: a.severity === "high" ? "#F87171" : "#F97316",
              border: "1px solid",
              borderColor: a.severity === "high" ? "rgba(248,113,113,0.35)" : "rgba(249,115,22,0.35)",
            }}>
              {a.severity === "high" ? "Urgente" : "Atenção"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
